import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Award } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { AgentInsightsEditor } from "@/components/AgentInsights";
import { AgentListingManager } from "@/components/agents/AgentListingManager";
import { BadgeVerificationPanel } from "@/components/ClaimWebsiteDialog";
import {
	DashboardContent,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { FounderStoryEditor } from "@/components/FounderStoryEditor";

export const Route = createFileRoute("/dashboard/manage")({
	validateSearch: z.object({ agentId: z.string().optional() }),
	component: DashboardManagePage,
});

type Tab = "listing" | "story" | "insights" | "badge";

const TABS: { id: Tab; label: string; requiresAgent?: boolean }[] = [
	{ id: "listing", label: "Listing" },
	{ id: "story", label: "Agent Story", requiresAgent: true },
	{ id: "insights", label: "Insights", requiresAgent: true },
	{ id: "badge", label: "Badge", requiresAgent: true },
];

function DashboardManagePage() {
	const { agentId } = Route.useSearch();
	const [activeTab, setActiveTab] = useState<Tab>("listing");

	return (
		<DashboardLayout>
			<DashboardContent className="max-w-3xl">
				<Link
					to="/dashboard"
					className="mb-5 inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
				>
					<ArrowLeft className="size-3" />
					Back to agents
				</Link>

				<div className="mb-5 flex border-b border-border">
					{TABS.map((tab) => {
						const disabled = tab.requiresAgent && !agentId;
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								type="button"
								disabled={disabled}
								onClick={() => setActiveTab(tab.id)}
								className={`border-b-2 px-4 py-2 font-mono text-sm uppercase tracking-[0.14em] transition-colors -mb-px disabled:pointer-events-none disabled:opacity-40 ${
									isActive
										? "border-brand text-brand"
										: "border-transparent text-muted-foreground hover:text-brand"
								}`}
							>
								{tab.label}
							</button>
						);
					})}
				</div>

				{activeTab === "listing" && (
					<AgentListingManager
						initialAgentId={agentId}
						title="Complete your listing"
						description="Fill in the details below to publish your agent on the leaderboard."
					/>
				)}

				{activeTab === "story" && agentId && (
					<section className="border border-border bg-card">
						<div className="border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Agent story
							</span>
						</div>
						<div className="space-y-4 p-5">
							<p className="text-base text-muted-foreground">
								A short structured note on what problem the agent solves, who
								the customers are, and what made it work.
							</p>
							<FounderStoryEditor agentId={agentId} />
						</div>
					</section>
				)}

				{activeTab === "insights" && agentId && (
					<section className="border border-border bg-card">
						<div className="border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Agent insights
							</span>
						</div>
						<div className="space-y-4 p-5">
							<p className="text-base text-muted-foreground">
								Edit or regenerate the AI-generated insights displayed on your
								agent's public profile.
							</p>
							<AgentInsightsEditor agentId={agentId} />
						</div>
					</section>
				)}

				{activeTab === "badge" && agentId && (
					<BadgeTabContent
						agentId={agentId}
						onGoToListing={() => setActiveTab("listing")}
					/>
				)}
			</DashboardContent>
		</DashboardLayout>
	);
}

type AgentListing = {
	agentId: string;
	name: string;
	slug: string;
	verificationStatus: string | null;
	website: string;
	websiteVerificationStatus: string;
};

function BadgeTabContent({
	agentId,
	onGoToListing,
}: {
	agentId: string;
	onGoToListing: () => void;
}) {
	const listingsQuery = useQuery({
		queryKey: ["agent-listings"],
		queryFn: async () => {
			const res = await fetch("/api/agents/listings", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch listings");
			return res.json() as Promise<{ agents: AgentListing[] }>;
		},
	});

	const agent = listingsQuery.data?.agents.find((a) => a.agentId === agentId);

	if (listingsQuery.isPending) {
		return (
			<div className="border border-border bg-card p-5">
				<div className="h-5 w-32 animate-pulse bg-muted" />
			</div>
		);
	}

	if (!agent || agent.verificationStatus !== "verified") {
		return (
			<section className="border border-border bg-card">
				<div className="border-b border-border px-5 py-3">
					<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; Badge
					</span>
				</div>
				<div className="flex items-start gap-4 p-5">
					<div className="flex size-9 shrink-0 items-center justify-center border border-border bg-background text-muted-foreground">
						<Award className="size-4" />
					</div>
					<div className="space-y-2">
						<h3
							className="font-mono text-base uppercase tracking-[0.08em] text-foreground"
							style={{ fontWeight: 400 }}
						>
							Badge available after verification
						</h3>
						<p className="text-base text-muted-foreground">
							Connect your Stripe account to verify your MRR. Once verified,
							you'll get an embeddable badge to add to your README or landing
							page.
						</p>
						<button
							type="button"
							onClick={onGoToListing}
							className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-brand transition-colors hover:text-brand-hover"
						>
							Go to Listing tab to connect Stripe →
						</button>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="border border-border bg-card">
			<div className="border-b border-border px-5 py-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Embeddable badge
				</span>
			</div>
			<BadgeVerificationPanel
				target={{
					agentSlug: agent.slug,
					agentName: agent.name,
					website: agent.website,
					currentStatus: agent.websiteVerificationStatus,
				}}
				onVerified={() => void listingsQuery.refetch()}
			/>
		</section>
	);
}
